import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Row, Col, Modal, Card, InputGroup } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiAlertCircle, FiUpload, FiLayers } from 'react-icons/fi';
import { useToast } from '../context/ToastContext';
import { API_BASE_URL } from '../config';

function StockManagement({ dbProducts, suppliers, onAddProduct, onUpdateProduct, onDeleteProduct }) {
  const showToast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('Jean Cartier');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, selectedCategory]);

  // Modals state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);

  // Group batch editing state
  const [currentGroup, setCurrentGroup] = useState(null);
  const [groupItemsState, setGroupItemsState] = useState([]);
  const [bulkCost, setBulkCost] = useState('');
  const [bulkSale, setBulkSale] = useState('');
  const [isSavingGroup, setIsSavingGroup] = useState(false);

  // Current editing product
  const [currentProduct, setCurrentProduct] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Product to delete
  const [productToDelete, setProductToDelete] = useState(null);

  // Form states
  const [formId, setFormId] = useState('');
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('Jean Cartier');
  const [formDescription, setFormDescription] = useState('');
  const [formNumeroFoto, setFormNumeroFoto] = useState('');
  const [formArticulo, setFormArticulo] = useState('');
  const [formDimensiones, setFormDimensiones] = useState('');
  const [formCostPrice, setFormCostPrice] = useState(0);
  const [formSalePrice, setFormSalePrice] = useState(0);
  const [formStock, setFormStock] = useState(0);
  const [formMinStock, setFormMinStock] = useState(0);
  const [formKit, setFormKit] = useState('');
  const [formColor, setFormColor] = useState('');
  const [formTamano, setFormTamano] = useState('');

  // Import JSON Modal states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // Category Tabs Configuration
  const CATEGORY_TABS = [
    { id: 'Jean Cartier', label: 'Jean Cartier' },
    { id: 'Mariages', label: 'Mariages' },
    { id: 'Essen', label: 'Essen' },
    { id: 'Otros', label: 'Otros' }
  ];

  // Helper function to match product to Tab
  const matchTab = (product, tabId) => {
    if (tabId === 'Jean Cartier') {
      return product.categoria === 'Jean Cartier' || product.categoria === 'Blanquería' || Boolean(product.numero_foto);
    }
    if (tabId === 'Mariages') {
      return product.categoria === 'Mariages';
    }
    if (tabId === 'Essen') {
      return product.categoria === 'Essen';
    }
    if (tabId === 'Otros') {
      return product.categoria === 'Otros' || (!['Jean Cartier', 'Blanquería', 'Mariages', 'Essen'].includes(product.categoria) && !product.numero_foto);
    }
    return true;
  };

  // List of existing kit names for autocomplete datalist
  const existingKits = Array.from(new Set(dbProducts.map(p => p.kit?.nombre).filter(Boolean)));

  // Summarize long dimensions
  const summarizeDimensions = (dim) => {
    if (!dim) return null;
    const trimmed = dim.trim();
    const matches = trimmed.match(/\d+\s*x\s*\d+(?:\s*x\s*\d+)?(?:\s*cm)?/gi);
    if (matches && matches.length > 0) {
      return matches.join(' | ');
    }
    return trimmed.length > 25 ? `${trimmed.substring(0, 25)}...` : trimmed;
  };

  // Filter and sort products by tab, search and photo number ASC
  const filteredProducts = dbProducts
    .filter(product => {
      const matchesTab = matchTab(product, activeTab);
      const matchesSearch =
        product.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.numero_foto && product.numero_foto.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.articulo && product.articulo.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.dimensiones && product.dimensiones.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.kit?.nombre && product.kit.nombre.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesTab && matchesSearch;
    })
    .sort((a, b) => {
      if (activeTab === 'Jean Cartier') {
        const photoA = a.numero_foto || '';
        const photoB = b.numero_foto || '';
        if (photoA && photoB) {
          return photoA.localeCompare(photoB, undefined, { numeric: true, sensitivity: 'base' });
        }
        if (photoA) return -1;
        if (photoB) return 1;
      }
      return (a.id || 0) - (b.id || 0);
    });

  // Group products by photo number for Jean Cartier
  const photoGroups = [];
  const groupMap = new Map();

  for (const p of filteredProducts) {
    const groupKey = (activeTab === 'Jean Cartier' && p.numero_foto) ? `photo-${p.numero_foto}` : `single-${p.id}`;
    if (!groupMap.has(groupKey)) {
      const newGroup = {
        key: groupKey,
        isGrouped: Boolean(activeTab === 'Jean Cartier' && p.numero_foto),
        numero_foto: p.numero_foto,
        nombre: p.nombre,
        categoria: p.categoria,
        color: p.color,
        items: []
      };
      groupMap.set(groupKey, newGroup);
      photoGroups.push(newGroup);
    }
    groupMap.get(groupKey).items.push(p);
  }

  // Predefined sizes for multi-size batch creation
  const PREDEFINED_SIZES = [
    { name: '1 plaza', suffix: '-1P' },
    { name: '1 1/2 plaza', suffix: '-1.5P' },
    { name: '2 plazas', suffix: '-2P' },
    { name: '2 1/2 plazas', suffix: '-2.5P' },
    { name: 'Queen', suffix: '-QUEEN' },
    { name: 'King', suffix: '-KING' }
  ];

  const [selectedSizes, setSelectedSizes] = useState({
    '1 plaza': { checked: false, stock: 10, codeSuffix: '-1P' },
    '1 1/2 plaza': { checked: false, stock: 10, codeSuffix: '-1.5P' },
    '2 plazas': { checked: false, stock: 10, codeSuffix: '-2P' },
    '2 1/2 plazas': { checked: false, stock: 10, codeSuffix: '-2.5P' },
    'Queen': { checked: false, stock: 10, codeSuffix: '-QUEEN' },
    'King': { checked: false, stock: 10, codeSuffix: '-KING' }
  });

  const resetSelectedSizes = () => {
    setSelectedSizes({
      '1 plaza': { checked: false, stock: 10, codeSuffix: '-1P' },
      '1 1/2 plaza': { checked: false, stock: 10, codeSuffix: '-1.5P' },
      '2 plazas': { checked: false, stock: 10, codeSuffix: '-2P' },
      '2 1/2 plazas': { checked: false, stock: 10, codeSuffix: '-2.5P' },
      'Queen': { checked: false, stock: 10, codeSuffix: '-QUEEN' },
      'King': { checked: false, stock: 10, codeSuffix: '-KING' }
    });
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentProduct(null);
    const defaultCat = activeTab;
    setFormId('');
    setFormName('');
    setFormCategory(defaultCat);
    setFormDescription('');
    setFormNumeroFoto('');
    setFormArticulo('');
    setFormDimensiones('');
    setFormCostPrice(0);
    setFormSalePrice(0);
    setFormStock(10);
    setFormMinStock(3);
    setFormKit('');
    setFormColor('');
    setFormTamano('');
    resetSelectedSizes();
    setShowAddEditModal(true);
  };

  const openEditModal = (product) => {
    setIsEditMode(true);
    setCurrentProduct(product);
    setFormId(product.codigo);
    setFormName(product.nombre);
    setFormCategory(product.categoria || activeTab);
    setFormDescription(product.descripcion || '');
    setFormNumeroFoto(product.numero_foto || '');
    setFormArticulo(product.articulo || '');
    setFormDimensiones(product.dimensiones || '');
    setFormCostPrice(parseFloat(product.precio_costo) || 0);
    setFormSalePrice(parseFloat(product.precio_venta) || 0);
    setFormStock(product.cantidad);
    setFormMinStock(product.umbral);
    setFormKit(product.kit?.nombre || '');
    setFormColor(product.color || '');
    setFormTamano(product.tamano || '');
    setShowAddEditModal(true);
  };

  const handleOpenGroupOrSingleModal = (group) => {
    if (group.isGrouped && group.items.length > 1) {
      setCurrentGroup(group);
      setGroupItemsState(group.items.map(item => ({
        ...item,
        cantidad: item.cantidad,
        precio_costo: parseFloat(item.precio_costo) || 0,
        precio_venta: parseFloat(item.precio_venta) || 0
      })));
      setBulkCost('');
      setBulkSale('');
      setShowGroupModal(true);
    } else {
      openEditModal(group.items[0]);
    }
  };

  const handleSaveGroupItems = async (e) => {
    e.preventDefault();
    try {
      setIsSavingGroup(true);
      for (const item of groupItemsState) {
        const cleanArt = item.articulo ? String(item.articulo).trim() : '';
        const newCode = (item.numero_foto && cleanArt) ? `${item.numero_foto}-${cleanArt}` : item.codigo;
        await onUpdateProduct({
          ...item,
          articulo: cleanArt,
          tamano: item.tamano ? String(item.tamano).trim() : null,
          dimensiones: item.dimensiones ? String(item.dimensiones).trim() : null,
          codigo: newCode,
          kit_nombre: item.kit?.nombre || undefined
        });
      }
      showToast(`Stock, artículo, medidas y precios de la foto ${currentGroup?.numero_foto || ''} actualizados con éxito.`, 'success');
      setShowGroupModal(false);
    } catch (err) {
      showToast('Error al actualizar las variantes del grupo.', 'error');
    } finally {
      setIsSavingGroup(false);
    }
  };

  const handleDeleteGroupVariant = async (itemToDelete) => {
    if (window.confirm(`¿Está seguro de eliminar la variante (${itemToDelete.tamano || 'Sin medida'} - Art. ${itemToDelete.articulo || itemToDelete.codigo})?`)) {
      try {
        await onDeleteProduct(itemToDelete.id);
        setGroupItemsState(prev => {
          const nextState = prev.filter(i => i.id !== itemToDelete.id);
          if (nextState.length === 0) {
            setShowGroupModal(false);
          }
          return nextState;
        });
        showToast('Variante eliminada con éxito.', 'success');
      } catch (err) {
        showToast('Error al eliminar la variante.', 'error');
      }
    }
  };

  const handleApplyBulkCost = () => {
    const cost = parseFloat(bulkCost);
    if (isNaN(cost) || cost < 0) return;
    setGroupItemsState(prev => prev.map(item => ({ ...item, precio_costo: cost })));
  };

  const handleApplyBulkSale = () => {
    const sale = parseFloat(bulkSale);
    if (isNaN(sale) || sale < 0) return;
    setGroupItemsState(prev => prev.map(item => ({ ...item, precio_venta: sale })));
  };

  const openDeleteModal = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      showToast('Por favor ingrese el nombre del producto.', 'warning');
      return;
    }

    const isMariages = formCategory === 'Mariages';
    const isJeanCartier = formCategory === 'Jean Cartier' || formCategory === 'Blanquería';

    // MULTI-SIZE BATCH MODE FOR JEAN CARTIER (ONLY IF SIZES ARE CHECKED)
    if (!isEditMode && isJeanCartier) {
      const activeSizes = Object.entries(selectedSizes).filter(([_, data]) => data.checked);
      if (activeSizes.length > 0) {
        let baseCode = formId.trim();
        if (!baseCode) {
          if (formNumeroFoto.trim() && formArticulo.trim()) {
            baseCode = `${formNumeroFoto.trim()}-${formArticulo.trim()}`;
          } else if (formNumeroFoto.trim()) {
            baseCode = formNumeroFoto.trim();
          } else if (formArticulo.trim()) {
            baseCode = formArticulo.trim();
          } else {
            baseCode = `JC-${Date.now()}`;
          }
        }

        for (const [sizeName, data] of activeSizes) {
          const variantCode = baseCode + data.codeSuffix;
          const isDup = dbProducts.some(p => p.codigo.trim().toLowerCase() === variantCode.toLowerCase());
          if (isDup) {
            showToast(`Ya existe un producto registrado con el código ${variantCode} (${sizeName}).`, 'error');
            return;
          }
        }

        for (const [sizeName, data] of activeSizes) {
          const variantCode = baseCode + data.codeSuffix;
          const productData = {
            codigo: variantCode,
            nombre: formName.trim(),
            categoria: formCategory,
            descripcion: formDescription.trim() || null,
            numero_foto: formNumeroFoto.trim() || null,
            articulo: formArticulo.trim() || null,
            dimensiones: formDimensiones.trim() || null,
            precio_costo: parseFloat(formCostPrice) || 0,
            precio_venta: parseFloat(formSalePrice) || 0,
            cantidad: parseInt(data.stock, 10) || 0,
            umbral: parseInt(formMinStock, 10) || 0,
            kit_nombre: formKit.trim(),
            color: formColor.trim() || null,
            tamano: sizeName
          };
          await onAddProduct(productData, true);
        }

        showToast(`Se crearon ${activeSizes.length} variantes de medidas con éxito.`, 'success');
        setShowAddEditModal(false);
        return;
      }
    }

    // SINGLE ITEM OR MULTI-VARIANT PROPERTY EDIT MODE
    let finalCode = formId.trim();
    if (!finalCode) {
      if (isMariages) {
        const cleanName = formName.trim().toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '');
        finalCode = `MARIAGES-${cleanName}-${Date.now().toString().slice(-4)}`;
      } else if (isJeanCartier) {
        if (formNumeroFoto.trim() && formArticulo.trim()) {
          finalCode = `${formNumeroFoto.trim()}-${formArticulo.trim()}`;
        } else if (formNumeroFoto.trim()) {
          finalCode = formNumeroFoto.trim();
        } else if (formArticulo.trim()) {
          finalCode = formArticulo.trim();
        } else {
          finalCode = `JC-${Date.now()}`;
        }
      } else {
        showToast('Por favor ingrese el código del producto.', 'warning');
        return;
      }
    }

    const productData = {
      id: currentProduct?.id,
      codigo: finalCode,
      nombre: formName.trim(),
      categoria: formCategory,
      descripcion: formDescription.trim() || null,
      numero_foto: isMariages ? null : (formNumeroFoto.trim() || null),
      articulo: isMariages ? null : (formArticulo.trim() || null),
      dimensiones: formDimensiones.trim() || null,
      precio_costo: parseFloat(formCostPrice) || 0,
      precio_venta: parseFloat(formSalePrice) || 0,
      cantidad: parseInt(formStock, 10) || 0,
      umbral: parseInt(formMinStock, 10) || 0,
      kit_nombre: isMariages ? null : formKit.trim(),
      color: formColor.trim() || null,
      tamano: isMariages ? null : (formTamano.trim() || null)
    };

    const isDuplicate = dbProducts.some(p => 
      p.id !== currentProduct?.id &&
      p.codigo.trim().toLowerCase() === finalCode.toLowerCase()
    );

    if (isDuplicate) {
      showToast(`Ya existe un producto registrado con el código ${finalCode}.`, 'error');
      return;
    }

    if (isEditMode) {
      if (currentProduct?.numero_foto) {
        // Update shared model properties across all variants sharing the same photo number,
        // without overwriting each variant's individual article, dimensions, size, or prices.
        const matchingVariants = dbProducts.filter(p => p.numero_foto === currentProduct.numero_foto);
        for (const variant of matchingVariants) {
          const isTarget = variant.id === currentProduct.id;
          await onUpdateProduct({
            ...variant,
            nombre: formName.trim(),
            categoria: formCategory,
            numero_foto: formNumeroFoto.trim() || null,
            descripcion: formDescription.trim() || null,
            kit_nombre: formKit.trim(),
            color: formColor.trim() || null,
            codigo: isTarget ? finalCode : variant.codigo,
            articulo: isTarget ? (formArticulo.trim() || variant.articulo) : variant.articulo,
            dimensiones: isTarget ? (formDimensiones.trim() || variant.dimensiones) : variant.dimensiones,
            tamano: isTarget ? (formTamano.trim() || variant.tamano) : variant.tamano,
            precio_costo: isTarget ? (parseFloat(formCostPrice) || 0) : variant.precio_costo,
            precio_venta: isTarget ? (parseFloat(formSalePrice) || 0) : variant.precio_venta,
            cantidad: isTarget ? (parseInt(formStock, 10) || 0) : variant.cantidad,
            umbral: isTarget ? (parseInt(formMinStock, 10) || 0) : variant.umbral
          });
        }
        showToast(`Datos del modelo ${currentProduct.numero_foto} actualizados con éxito.`, 'success');
      } else {
        await onUpdateProduct(productData);
      }
    } else {
      await onAddProduct(productData);
    }
    setShowAddEditModal(false);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      onDeleteProduct(productToDelete.id);
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
  };

  const handleImportCatalogSubmit = async () => {
    if (!importJsonText.trim()) {
      showToast('Por favor pegue el contenido JSON del catálogo.', 'warning');
      return;
    }
    try {
      setIsImporting(true);
      const parsed = JSON.parse(importJsonText);
      const response = await fetch(`${API_BASE_URL}/api/productos/importar-catalogo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(parsed)
      });
      if (response.ok) {
        const resData = await response.json();
        showToast(resData.message || 'Catálogo importado con éxito.', 'success');
        setShowImportModal(false);
        setImportJsonText('');
        window.location.reload();
      } else {
        const errData = await response.json();
        showToast(errData.error || 'Error al importar catálogo.', 'error');
      }
    } catch (err) {
      showToast('Formato JSON inválido. Verifique la estructura.', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(photoGroups.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGroups = photoGroups.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold m-0" style={{ color: 'var(--primary-color)' }}>
          Gestión de Inventario / Stock
        </h2>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => setShowImportModal(true)}
            className="d-flex align-items-center gap-2 border shadow-sm bg-white"
            style={{ color: 'var(--primary-color)', borderColor: '#cbd5e1' }}
          >
            <FiUpload /> Importar Catálogo JSON
          </Button>
          <Button
            variant="primary"
            onClick={openAddModal}
            className="d-flex align-items-center gap-2 border-0 shadow-sm"
            style={{ backgroundColor: 'var(--accent-color)', color: '#ffffff' }}
          >
            <FiPlus /> Agregar Producto
          </Button>
        </div>
      </div>

      {/* Category Tab Navs */}
      <div className="d-flex gap-2 mb-3 pb-1 border-bottom">
        {CATEGORY_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = dbProducts.filter(p => matchTab(p, tab.id)).length;
          return (
            <Button
              key={tab.id}
              variant={isActive ? 'primary' : 'light'}
              className={`px-4 py-2 fw-bold rounded-pill d-flex align-items-center gap-2 border ${isActive ? 'shadow-sm' : 'text-muted'}`}
              style={{
                backgroundColor: isActive ? 'var(--primary-color)' : '#f8fafc',
                borderColor: isActive ? 'var(--primary-color)' : '#e2e8f0',
                color: isActive ? '#ffffff' : '#64748b',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              <span className={`badge rounded-pill ${isActive ? 'bg-white text-dark' : 'bg-secondary text-white'}`} style={{ fontSize: '0.75rem' }}>
                {count}
              </span>
            </Button>
          );
        })}
      </div>

      {/* Search Bar Area */}
      <Card className="custom-card border-0 mb-4">
        <Card.Body className="p-2">
          <InputGroup>
            <InputGroup.Text className="bg-white border-end-0">
              <FiSearch color="var(--text-muted)" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={`Buscar en ${activeTab} por Código, Nº Foto, Nombre o Medida...`}
              className="border-start-0 ps-0"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      {/* Stock Table */}
      <Card className="custom-card border-0">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 text-nowrap">
              <thead className="bg-light">
                <tr>
                  {activeTab === 'Jean Cartier' && (
                    <th className="py-3 text-center" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Nº Foto</th>
                  )}
                  <th className="py-3" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Nombre / Color</th>
                  <th className="py-3" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Kit</th>
                  <th className="py-3" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Medida / Dimensiones</th>
                  <th className="py-3 text-center" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Stock</th>
                  <th className="py-3 text-center" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Mín. Stock</th>
                  <th className="py-3 text-end" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Costo</th>
                  <th className="py-3 text-end" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Venta</th>
                  <th className="py-3 text-center" style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Estado</th>
                  <th className="px-4 py-3 text-center" style={{ width: '120px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {photoGroups.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'Jean Cartier' ? 10 : 9} className="text-center py-5 text-muted">
                      No se encontraron productos en la categoría <strong>{activeTab}</strong>.
                    </td>
                  </tr>
                ) : (
                  currentGroups.map((group) => {
                    const isMultiple = group.isGrouped && group.items.length > 1;
                    const firstItem = group.items[0];

                    const totalStock = group.items.reduce((acc, i) => acc + i.cantidad, 0);
                    const isAnyLowStock = group.items.some(i => i.cantidad <= i.umbral);

                    // Check costs and sales equality
                    const costs = group.items.map(i => parseFloat(i.precio_costo) || 0);
                    const allCostsEqual = costs.every(c => c === costs[0]);

                    const sales = group.items.map(i => parseFloat(i.precio_venta) || 0);
                    const allSalesEqual = sales.every(s => s === sales[0]);

                    const sizesList = group.items.map(i => i.tamano || 'Sin Medida').join(', ');
                    const groupKitName = group.items.find(i => i.kit?.nombre)?.kit?.nombre;

                    return (
                      <tr
                        key={group.key}
                        className={isAnyLowStock ? 'table-warning-custom' : ''}
                        onClick={() => handleOpenGroupOrSingleModal(group)}
                        style={{ cursor: 'pointer' }}
                        title={isMultiple ? "Haga clic para cargar stock y precios por medida" : "Haga clic para editar stock"}
                      >
                        {activeTab === 'Jean Cartier' && (
                          <td className="text-center font-monospace fw-bold">
                            {group.numero_foto ? (
                              <span className="badge bg-light text-dark border font-monospace" style={{ fontSize: '0.85rem' }}>
                                {group.numero_foto}
                              </span>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                        )}
                        <td className="fw-semibold" style={{ color: 'var(--primary-color)' }}>
                          {group.nombre}
                        </td>
                        <td>
                          {groupKitName ? (
                            <span
                              className="badge bg-light text-dark border font-monospace fw-normal text-wrap text-start"
                              style={{ fontSize: '0.78rem', maxWidth: '220px', display: 'inline-block', lineHeight: '1.3' }}
                              title={groupKitName}
                            >
                              <FiLayers className="me-1 text-primary" size={12} />
                              {groupKitName}
                            </span>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td>
                          {isMultiple ? (
                            <div>
                              <div className="fw-semibold text-truncate" style={{ maxWidth: '220px' }} title={sizesList}>
                                {sizesList}
                              </div>
                              {firstItem.dimensiones && (
                                <div
                                  className="text-muted small font-monospace text-truncate"
                                  style={{ fontSize: '0.75rem', maxWidth: '190px' }}
                                  title={firstItem.dimensiones}
                                >
                                  {summarizeDimensions(firstItem.dimensiones)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div className="fw-semibold">{firstItem.tamano || '-'}</div>
                              {firstItem.dimensiones && (
                                <div
                                  className="text-muted small font-monospace text-truncate"
                                  style={{ fontSize: '0.75rem', maxWidth: '190px' }}
                                  title={firstItem.dimensiones}
                                >
                                  {summarizeDimensions(firstItem.dimensiones)}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="text-center fw-bold">
                          {totalStock}
                          {isAnyLowStock && (
                            <span className="ms-1 text-danger" title="¡Alerta! Stock por debajo del mínimo">
                              <FiAlertCircle />
                            </span>
                          )}
                        </td>
                        <td className="text-center text-muted">{firstItem.umbral}</td>
                        <td className="text-end font-monospace">
                          {allCostsEqual ? (
                            `$${costs[0].toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                          ) : (
                            <div className="d-flex flex-column align-items-end" style={{ fontSize: '0.78rem' }}>
                              {group.items.map((item, idx) => (
                                <div key={idx} className="text-nowrap" title={`${item.tamano}: $${parseFloat(item.precio_costo).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}>
                                  <span className="text-muted me-1 small">{item.tamano || 'P'}:</span>
                                  <span>${(parseFloat(item.precio_costo) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="text-end font-monospace fw-semibold" style={{ color: 'var(--accent-color)' }}>
                          {allSalesEqual ? (
                            `$${sales[0].toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                          ) : (
                            <div className="d-flex flex-column align-items-end" style={{ fontSize: '0.78rem' }}>
                              {group.items.map((item, idx) => (
                                <div key={idx} className="text-nowrap" title={`${item.tamano}: $${parseFloat(item.precio_venta).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}>
                                  <span className="text-muted me-1 small">{item.tamano || 'P'}:</span>
                                  <span>${(parseFloat(item.precio_venta) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="text-center">
                          {isAnyLowStock ? (
                            <span className="badge bg-danger">Stock Bajo</span>
                          ) : (
                            <span className="badge bg-success">Ok</span>
                          )}
                        </td>
                        <td className="px-4 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-2 justify-content-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(firstItem);
                              }}
                              className="p-1 d-flex align-items-center"
                              title="Editar datos del producto (Nombre, Foto, Categoría, etc.)"
                            >
                              <FiEdit2 size={14} />
                            </Button>
                            {!isMultiple && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(firstItem);
                                }}
                                className="p-1 d-flex align-items-center"
                                title="Eliminar producto"
                              >
                                <FiTrash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3 no-print px-3 pb-2">
              <span className="text-muted small">
                Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, photoGroups.length)} de {photoGroups.length} modelos de productos
              </span>
              <div className="d-flex gap-1">
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  style={{ borderRadius: '6px' }}
                >
                  Anterior
                </Button>
                <span className="btn btn-sm btn-light fw-bold disabled" style={{ borderRadius: '6px' }}>
                  Pág. {currentPage} de {totalPages}
                </span>
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  style={{ borderRadius: '6px' }}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Group Multi-Variant Batch Edit Modal */}
      <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)} centered size="xl">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: 'var(--primary-color)' }}>
            Editar Stock y Precios (Foto {currentGroup?.numero_foto} - {currentGroup?.nombre})
            {groupItemsState[0]?.kit?.nombre && (
              <div className="text-muted small fw-normal mt-1" style={{ fontSize: '0.85rem' }}>
                <FiLayers className="me-1 text-primary" size={14} /> Kit: {groupItemsState[0].kit.nombre}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveGroupItems}>
          <Modal.Body className="pt-2">
            {/* Quick Bulk Pricing Helper */}
            <Card className="border bg-light p-3 mb-3 rounded-3">
              <div className="fw-bold small text-muted mb-2">ASIGNAR PRECIOS MASIVOS A TODAS LAS MEDIDAS (OPCIONAL)</div>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label className="small text-muted fw-semibold">Asignar Precio de Costo a todas</Form.Label>
                  <InputGroup size="sm">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="Ej: 15000"
                      value={bulkCost}
                      onChange={(e) => setBulkCost(e.target.value)}
                    />
                    <Button variant="outline-primary" onClick={handleApplyBulkCost}>
                      Aplicar a todas
                    </Button>
                  </InputGroup>
                </Col>
                <Col md={6}>
                  <Form.Label className="small text-muted fw-semibold">Asignar Precio de Venta a todas</Form.Label>
                  <InputGroup size="sm">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="Ej: 25000"
                      value={bulkSale}
                      onChange={(e) => setBulkSale(e.target.value)}
                    />
                    <Button variant="outline-primary" onClick={handleApplyBulkSale}>
                      Aplicar a todas
                    </Button>
                  </InputGroup>
                </Col>
              </Row>
            </Card>

            {/* Variant Items Table */}
            <div className="table-responsive">
              <Table hover className="align-middle mb-0 text-nowrap border">
                <thead className="bg-light">
                  <tr>
                    <th className="py-2" style={{ width: '130px' }}>Medida / Talle</th>
                    <th className="py-2" style={{ width: '145px' }}>Artículo</th>
                    <th className="py-2">Dimensiones</th>
                    <th className="py-2 text-center" style={{ width: '110px' }}>Stock Actual</th>
                    <th className="py-2 text-center" style={{ width: '135px' }}>Precio Costo ($)</th>
                    <th className="py-2 text-center" style={{ width: '135px' }}>Precio Venta ($)</th>
                    <th className="py-2 text-center" style={{ width: '70px' }}>Eliminar</th>
                  </tr>
                </thead>
                <tbody>
                  {groupItemsState.map((item, index) => (
                    <tr key={item.id}>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          className="fw-bold"
                          style={{ minWidth: '110px', color: 'var(--primary-color)' }}
                          value={item.tamano || ''}
                          placeholder="Medida"
                          onChange={(e) => {
                            const val = e.target.value;
                            setGroupItemsState(prev => {
                              const updated = [...prev];
                              updated[index] = { ...updated[index], tamano: val };
                              return updated;
                            });
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          className="font-monospace fw-semibold"
                          style={{ minWidth: '130px' }}
                          value={item.articulo || ''}
                          placeholder="Artículo"
                          onChange={(e) => {
                            const val = e.target.value;
                            setGroupItemsState(prev => {
                              const updated = [...prev];
                              updated[index] = { ...updated[index], articulo: val };
                              return updated;
                            });
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          className="small text-muted font-monospace"
                          style={{ minWidth: '220px' }}
                          value={item.dimensiones || ''}
                          placeholder="Dimensiones (ej: 240 x 240 cm)"
                          onChange={(e) => {
                            const val = e.target.value;
                            setGroupItemsState(prev => {
                              const updated = [...prev];
                              updated[index] = { ...updated[index], dimensiones: val };
                              return updated;
                            });
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          className="text-center fw-bold"
                          value={item.cantidad}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10) || 0;
                            setGroupItemsState(prev => {
                              const updated = [...prev];
                              updated[index] = { ...updated[index], cantidad: val };
                              return updated;
                            });
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          step="0.01"
                          className="text-center font-monospace"
                          value={item.precio_costo}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setGroupItemsState(prev => {
                              const updated = [...prev];
                              updated[index] = { ...updated[index], precio_costo: val };
                              return updated;
                            });
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          step="0.01"
                          className="text-center font-monospace fw-bold text-success"
                          value={item.precio_venta}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setGroupItemsState(prev => {
                              const updated = [...prev];
                              updated[index] = { ...updated[index], precio_venta: val };
                              return updated;
                            });
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="p-1 d-flex align-items-center justify-content-center mx-auto"
                          onClick={() => handleDeleteGroupVariant(item)}
                          title="Eliminar esta variante"
                        >
                          <FiTrash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowGroupModal(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSavingGroup}
              style={{ backgroundColor: 'var(--accent-color)', borderColor: 'var(--accent-color)' }}
            >
              {isSavingGroup ? 'Guardando...' : 'Guardar Todos los Cambios'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add / Edit Single Product Modal */}
      <Modal show={showAddEditModal} onHide={() => setShowAddEditModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: 'var(--primary-color)' }}>
            {isEditMode ? `Editar Propiedades del Producto: ${formName}` : `Agregar Nuevo Producto`}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveProduct}>
          <Modal.Body className="pt-2">
            <Row className="g-3">
              {/* 1. CATEGORÍA AT THE VERY TOP */}
              <Col md={12}>
                <Form.Group className="bg-light p-2.5 rounded border">
                  <Form.Label className="fw-bold text-dark mb-1">CATEGORÍA DEL PRODUCTO</Form.Label>
                  <Form.Select
                    className="fw-semibold"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                  >
                    <option value="Jean Cartier">Jean Cartier</option>
                    <option value="Mariages">Mariages</option>
                    <option value="Essen">Essen</option>
                    <option value="Otros">Otros</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* MARIAGES CATEGORY FORM */}
              {formCategory === 'Mariages' ? (
                <>
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">NOMBRE DEL PRODUCTO</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        placeholder="Ej: Juego de sábanas Mariages"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">COLOR (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: Blanco, Beig, etc."
                        value={formColor}
                        onChange={(e) => setFormColor(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">DIMENSIONES (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: 160 x 240 cm"
                        value={formDimensiones}
                        onChange={(e) => setFormDimensiones(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">STOCK ACTUAL</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        required
                        value={formStock}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) setFormStock(val);
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">DESCRIPCIÓN (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Descripción o notas del producto"
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </>
              ) : (formCategory === 'Jean Cartier' || formCategory === 'Blanquería') ? (
                /* JEAN CARTIER CATEGORY FORM */
                <>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">Nº FOTO / COLOR</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: 004"
                        value={formNumeroFoto}
                        onChange={(e) => setFormNumeroFoto(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">ARTÍCULO</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: 0967/3"
                        value={formArticulo}
                        onChange={(e) => setFormArticulo(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">NOMBRE DEL PRODUCTO / COLOR</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        placeholder="Ej: Cortina Nepal Asfalto"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">CÓDIGO DE BARRAS / SISTEMA (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        disabled={isEditMode}
                        placeholder="Opcional (se auto-genera si se deja vacío)"
                        value={formId}
                        onChange={(e) => setFormId(e.target.value.toUpperCase())}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">DIMENSIONES (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: 140 x 210 cm"
                        value={formDimensiones}
                        onChange={(e) => setFormDimensiones(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">TAMAÑO / MEDIDA (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: 140x210 cm, Cortina, Queen, etc."
                        value={formTamano}
                        onChange={(e) => setFormTamano(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">STOCK ACTUAL (INDIVIDUAL)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        required
                        value={formStock}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) setFormStock(val);
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">PERTENECE A UN KIT (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Kit al que pertenece el producto"
                        list="kits-list"
                        value={formKit}
                        onChange={(e) => setFormKit(e.target.value)}
                      />
                      <datalist id="kits-list">
                        {existingKits.map((k, index) => (
                          <option key={index} value={k} />
                        ))}
                      </datalist>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">COLOR (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Color del producto"
                        value={formColor}
                        onChange={(e) => setFormColor(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">DESCRIPCIÓN</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Descripción o notas del producto"
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  {!isEditMode && (
                    <Col md={12}>
                      <Card className="border bg-light p-3 rounded-3 my-2">
                        <div className="fw-bold mb-2 text-dark d-flex justify-content-between align-items-center">
                          <span style={{ color: 'var(--primary-color)' }}>
                            SELECCIONAR MEDIDAS Y STOCK EN LOTE (OPCIONAL)
                          </span>
                          <span className="small text-muted fw-normal">
                            Solo si desea crear variantes de sábanas/acolchados. Si no tilda ninguna, se creará un único producto con el Stock Actual.
                          </span>
                        </div>
                        <Row className="g-2">
                          {PREDEFINED_SIZES.map((sizeObj) => {
                            const sizeData = selectedSizes[sizeObj.name] || { checked: false, stock: 10, codeSuffix: sizeObj.suffix };
                            return (
                              <Col md={6} key={sizeObj.name}>
                                <div className={`p-2 rounded border bg-white d-flex align-items-center justify-content-between gap-2 ${sizeData.checked ? 'border-primary shadow-sm' : 'opacity-75'}`}>
                                  <Form.Check
                                    type="checkbox"
                                    id={`size-check-${sizeObj.name}`}
                                    label={<strong className="small">{sizeObj.name}</strong>}
                                    checked={sizeData.checked}
                                    onChange={(e) => {
                                      setSelectedSizes(prev => ({
                                        ...prev,
                                        [sizeObj.name]: { ...prev[sizeObj.name], checked: e.target.checked }
                                      }));
                                    }}
                                  />
                                  {sizeData.checked && (
                                    <div className="d-flex align-items-center gap-1">
                                      <span className="small text-muted" style={{ fontSize: '0.75rem' }}>Stock:</span>
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min="0"
                                        style={{ width: '75px' }}
                                        value={sizeData.stock}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (/^\d*$/.test(val)) {
                                            setSelectedSizes(prev => ({
                                              ...prev,
                                              [sizeObj.name]: { ...prev[sizeObj.name], stock: val }
                                            }));
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </Card>
                    </Col>
                  )}
                </>
              ) : (
                /* ESSEN AND OTROS CATEGORY FORM */
                <>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">CÓDIGO DE BARRAS / SISTEMA</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        disabled={isEditMode}
                        placeholder="Código único del producto"
                        value={formId}
                        onChange={(e) => setFormId(e.target.value.toUpperCase())}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">NOMBRE DEL PRODUCTO</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        placeholder="Ej: Cacerola 24cm Essen"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">TAMAÑO / MEDIDA (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: 24 cm / Mediano"
                        value={formTamano}
                        onChange={(e) => setFormTamano(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">COLOR (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Color del producto"
                        value={formColor}
                        onChange={(e) => setFormColor(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">PERTENECE A UN KIT (OPCIONAL)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Kit al que pertenece"
                        list="kits-list"
                        value={formKit}
                        onChange={(e) => setFormKit(e.target.value)}
                      />
                      <datalist id="kits-list">
                        {existingKits.map((k, index) => (
                          <option key={index} value={k} />
                        ))}
                      </datalist>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">STOCK ACTUAL</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        required
                        value={formStock}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) setFormStock(val);
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-muted small">DESCRIPCIÓN</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Descripción o notas del producto"
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}

              {/* SHARED PRICING AND ALERT FIELDS FOR ALL CATEGORIES */}
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small">STOCK MÍNIMO (ALERTA)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    required
                    value={formMinStock}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) setFormMinStock(val);
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small">PRECIO DE COSTO ($)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={formCostPrice}
                    onChange={(e) => {
                      const val = e.target.value.replace(',', '.');
                      if (/^\d*\.?\d*$/.test(val)) setFormCostPrice(val);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small">PRECIO DE VENTA ($)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={formSalePrice}
                    onChange={(e) => {
                      const val = e.target.value.replace(',', '.');
                      if (/^\d*\.?\d*$/.test(val)) setFormSalePrice(val);
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowAddEditModal(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              style={{ backgroundColor: 'var(--accent-color)', borderColor: 'var(--accent-color)' }}
            >
              {isEditMode ? 'Guardar Cambios' : 'Crear Producto'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-danger">Eliminar Producto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Está seguro de que desea eliminar el producto <strong>{productToDelete?.nombre}</strong> (Cod: {productToDelete?.codigo})?
          <p className="text-muted small mt-2">Esta acción no se puede deshacer.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Catalog JSON Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: 'var(--primary-color)' }}>
            Importar Catálogo Blanquería (JSON)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="text-muted small mb-2">
            Pegue a continuación el código JSON del catálogo (con estructura <code>"001": &#123; "nombre": "...", "medidas": [...] &#125;</code>). Se precargarán todas las variantes en la base de datos dentro de la categoría <strong>Jean Cartier</strong>.
          </p>
          <Form.Group>
            <Form.Control
              as="textarea"
              rows={12}
              className="font-monospace small"
              placeholder='{\n  "001": {\n    "nombre": "GINEBRA NATURAL",\n    "medidas": [\n      {"medida": "Queen", "articulo": "0949/30", "dimensiones": "240 x 240 cm"}\n    ]\n  }\n}'
              value={importJsonText}
              onChange={(e) => setImportJsonText(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isImporting}
            onClick={handleImportCatalogSubmit}
            style={{ backgroundColor: 'var(--accent-color)', borderColor: 'var(--accent-color)' }}
          >
            {isImporting ? 'Importando...' : 'Procesar e Importar Catálogo'}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .table-warning-custom {
          background-color: #fffbeb !important;
        }
        .table-warning-custom:hover {
          background-color: #fef3c7 !important;
        }
        .text-teal {
          color: var(--accent-color) !important;
        }
      `}</style>
    </div>
  );
}

export default StockManagement;
